import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const marketSubscribedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const connect = useCallback(() => {
    const wsUrl = (import.meta.env.VITE_WS_URL || 'ws://localhost:4445') + '/ws';
    
    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        reconnectAttempts.current = 0;
        
        // Resubscribe to market data if we were previously subscribed
        if (marketSubscribedRef.current) {
          socket.send(JSON.stringify({ type: 'subscribe:market' }));
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          // Handle market data updates
          if (data.type === 'market:updated') {
            setMarketData(data.data);
          }
          
          setLastMessage(data);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            connect();
          }, delay);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (socketRef.current) {
      socketRef.current.close();
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Subscribe to transaction updates
  const subscribeToTransaction = useCallback((transactionId) => {
    console.log('Subscribing to transaction:', transactionId);
    sendMessage({
      type: 'subscribe:transaction',
      transactionId,
    });
  }, [sendMessage]);

  // Unsubscribe from transaction updates
  const unsubscribeFromTransaction = useCallback((transactionId) => {
    sendMessage({
      type: 'unsubscribe:transaction',
      transactionId,
    });
  }, [sendMessage]);

  // Subscribe to market data updates
  const subscribeToMarket = useCallback(() => {
    console.log('Subscribing to market data');
    marketSubscribedRef.current = true;
    sendMessage({
      type: 'subscribe:market',
    });
  }, [sendMessage]);

  // Unsubscribe from market data updates
  const unsubscribeFromMarket = useCallback(() => {
    console.log('Unsubscribing from market data');
    marketSubscribedRef.current = false;
    sendMessage({
      type: 'unsubscribe:market',
    });
  }, [sendMessage]);

  useEffect(() => {
    connect();
    
    return () => disconnect();
  }, [connect, disconnect]);

  const value = {
    connected,
    lastMessage,
    marketData,
    sendMessage,
    subscribeToTransaction,
    unsubscribeFromTransaction,
    subscribeToMarket,
    unsubscribeFromMarket,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
